import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import RetouchTypesManagement from './RetouchTypesManagement';

const ModelsManagement = () => {
  const [models, setModels] = useState([]);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [selectedModelForRetouches, setSelectedModelForRetouches] = useState(null);
  const [formData, setFormData] = useState({
    name: '', production_lines: [], description: '', is_active: true, image: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [modelsRes, linesRes] = await Promise.all([
        axios.get(`${API_URL}/api/models/`),
        axios.get(`${API_URL}/api/production-lines/`)
      ]);
      setModels(modelsRes.data.results || modelsRes.data);
      setLines(linesRes.data.results || linesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.production_lines.length === 0) {
        alert('Veuillez sélectionner au moins une chaîne de production');
        return;
      }

      const data = new FormData();
      data.append('name', formData.name);
      data.append('description', formData.description || '');
      data.append('is_active', formData.is_active);
      
      formData.production_lines.forEach(lineId => {
        data.append('production_lines', lineId);
      });
      
      if (formData.image) {
        data.append('image', formData.image);
      }

      if (editingModel) {
        await axios.patch(`${API_URL}/api/models/${editingModel.id}/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_URL}/api/models/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      fetchData();
      setShowForm(false);
      setEditingModel(null);
      setFormData({ name: '', production_lines: [], description: '', is_active: true, image: null });
    } catch (error) {
      console.error('Erreur complète:', error.response?.data);
      alert('Erreur: ' + (error.response?.data?.detail || JSON.stringify(error.response?.data) || 'Erreur inconnue'));
    }
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    let productionLineIds = [];
    if (model.production_lines && Array.isArray(model.production_lines)) {
      productionLineIds = model.production_lines.map(line => {
        if (typeof line === 'object' && line.id) {
          return line.id;
        }
        return line;
      });
    }
    setFormData({
      name: model.name,
      production_lines: productionLineIds,
      description: model.description || '',
      is_active: model.is_active,
      image: null
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce modèle ?')) {
      try {
        await axios.delete(`${API_URL}/api/models/${id}/`);
        fetchData();
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner h-12 w-12"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header amélioré */}
      <div className="bg-gradient-primary rounded-xl shadow-corporate-xl p-6 sm:p-8 text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2">Gestion des Modèles</h2>
            <p className="text-white/80 text-sm sm:text-base">Créez et gérez vos modèles de produits</p>
          </div>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingModel(null);
              setFormData({ name: '', production_lines: [], description: '', is_active: true, image: null });
            }}
            className="btn-secondary bg-white/10 hover:bg-white/20 border-white/30 text-white flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Ajouter un modèle</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={() => { setShowForm(false); setEditingModel(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
              <h3 className="text-xl sm:text-2xl font-bold text-corporate-900">
                {editingModel ? 'Modifier' : 'Ajouter'} un modèle
              </h3>
              <button
                onClick={() => { setShowForm(false); setEditingModel(null); }}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-600"
                aria-label="Fermer"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="input-modern"
                  placeholder="Nom du modèle"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-3">
                  Chaînes de production * (sélection multiple)
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200 max-h-[300px] overflow-y-auto">
                  {lines.map(line => {
                    const isSelected = formData.production_lines.includes(line.id);
                    return (
                      <button
                        key={line.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setFormData({
                              ...formData,
                              production_lines: formData.production_lines.filter(id => id !== line.id)
                            });
                          } else {
                            setFormData({
                              ...formData,
                              production_lines: [...formData.production_lines, line.id]
                            });
                          }
                        }}
                        className={`
                          p-3 rounded-lg border-2 transition-all duration-200 text-left
                          ${isSelected
                            ? 'bg-primary-600 border-primary-700 text-white shadow-corporate'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-primary-400 hover:bg-primary-50'
                          }
                        `}
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0
                            ${isSelected ? 'border-white bg-white' : 'border-gray-400'}
                          `}>
                            {isSelected && (
                              <svg className="w-3 h-3 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm font-medium truncate">{line.name}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {formData.production_lines.length === 0 && (
                  <p className="text-xs text-red-600 mt-2">Veuillez sélectionner au moins une chaîne</p>
                )}
                {formData.production_lines.length > 0 && (
                  <p className="text-xs text-gray-600 mt-2">
                    {formData.production_lines.length} chaîne{formData.production_lines.length > 1 ? 's' : ''} sélectionnée{formData.production_lines.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-2">
                  Image
                </label>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* Prendre une photo */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                      className="hidden"
                      id="model-image-camera"
                    />
                    <label htmlFor="model-image-camera" className="cursor-pointer block">
                      <div className="text-3xl mb-2">📷</div>
                      <p className="text-xs text-gray-600 font-medium">Prendre une photo</p>
                    </label>
                  </div>
                  
                  {/* Uploader depuis galerie */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary-400 transition-colors cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                      className="hidden"
                      id="model-image-gallery"
                    />
                    <label htmlFor="model-image-gallery" className="cursor-pointer block">
                      <div className="text-3xl mb-2">🖼️</div>
                      <p className="text-xs text-gray-600 font-medium">Depuis la galerie</p>
                    </label>
                  </div>
                </div>
                
                {formData.image && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">
                      ✓ {formData.image.name}
                    </p>
                  </div>
                )}
                
                {editingModel && editingModel.image_url && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-2">Image actuelle :</p>
                    <img src={editingModel.image_url} alt="Prévisualisation" className="rounded-lg shadow-corporate max-w-full sm:max-w-xs" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-corporate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  className="input-modern"
                  placeholder="Description du modèle..."
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_active_model"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="is_active_model" className="text-sm font-semibold text-corporate-700">
                  Modèle actif
                </label>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 pt-4 border-t border-gray-200">
                <button type="submit" className="btn-primary flex-1">
                  {editingModel ? 'Enregistrer' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingModel(null); }}
                  className="btn-secondary flex-1"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {models.length === 0 ? (
        <div className="card text-center py-16 border-0 shadow-corporate-lg">
          <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 text-4xl">
            📦
          </div>
          <h3 className="text-xl font-bold text-corporate-900 mb-2">Aucun modèle</h3>
          <p className="text-gray-600 mb-6">Commencez par créer votre premier modèle</p>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingModel(null);
              setFormData({ name: '', production_lines: [], description: '', is_active: true, image: null });
            }}
            className="btn-primary"
          >
            Créer un modèle
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {models.map((model, index) => (
            <div
              key={model.id}
              className="card-hover border-0 shadow-corporate-lg animate-fade-in group"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {model.image_url ? (
                <div className="relative overflow-hidden rounded-lg mb-4 h-48 bg-gray-100">
                  <img
                    src={model.image_url}
                    alt={model.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-3 right-3">
                    {model.is_active ? (
                      <span className="badge-success shadow-corporate">Actif</span>
                    ) : (
                      <span className="badge-danger shadow-corporate">Inactif</span>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              ) : (
                <div className="relative h-48 mb-4 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <div className="text-6xl opacity-50">📦</div>
                  <div className="absolute top-3 right-3">
                    {model.is_active ? (
                      <span className="badge-success shadow-corporate">Actif</span>
                    ) : (
                      <span className="badge-danger shadow-corporate">Inactif</span>
                    )}
                  </div>
                </div>
              )}
              
              <h3 className="text-lg font-bold text-corporate-900 mb-3 group-hover:text-primary-600 transition-colors">
                {model.name}
              </h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex items-start space-x-3 p-2 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Chaînes</p>
                    <p className="text-sm text-gray-900 line-clamp-2">
                      {model.production_lines_names && model.production_lines_names.length > 0
                        ? model.production_lines_names.join(', ')
                        : 'Non spécifiée'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-2 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 1.756 3.35 0 .426-1.756 2.924-1.756 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Retouches</p>
                    <p className="text-sm text-gray-900">
                      {model.retouch_types_names && model.retouch_types_names.length > 0
                        ? `${model.retouch_types_names.length} type(s)`
                        : 'Aucune'}
                    </p>
                  </div>
                </div>
              </div>

              {model.description && (
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{model.description}</p>
              )}

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-1.5 sm:gap-1.5 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleEdit(model)}
                  className="flex-1 min-w-0 bg-gradient-primary text-white font-semibold rounded-lg shadow-corporate hover:shadow-corporate-lg transform hover:-translate-y-0.5 transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 text-xs sm:text-sm py-2 px-2 sm:px-3 truncate"
                >
                  Modifier
                </button>
                <button
                  onClick={() => setSelectedModelForRetouches(model)}
                  className="flex-1 min-w-0 bg-white text-corporate-700 font-semibold rounded-lg border-2 border-corporate-200 shadow-corporate hover:shadow-corporate-lg hover:border-corporate-300 transform hover:-translate-y-0.5 transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-corporate-500 focus:ring-offset-2 text-xs sm:text-sm py-2 px-2 sm:px-3 truncate"
                >
                  Retouches
                </button>
                <button
                  onClick={() => handleDelete(model.id)}
                  className="flex-1 min-w-0 bg-red-600 text-white font-semibold rounded-lg shadow-corporate hover:shadow-corporate-lg hover:bg-red-700 transform hover:-translate-y-0.5 transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-xs sm:text-sm py-2 px-2 sm:px-3 truncate"
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedModelForRetouches && (
        <div className="modal-overlay" onClick={() => setSelectedModelForRetouches(null)}>
          <div className="modal-content max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-corporate-900">
                  Gestion des Retouches
                </h2>
                <p className="text-gray-600 text-sm mt-1">Modèle: {selectedModelForRetouches.name}</p>
              </div>
              <button
                onClick={() => setSelectedModelForRetouches(null)}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors text-gray-600"
                aria-label="Fermer"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <RetouchTypesManagement
              modelId={selectedModelForRetouches.id}
              onClose={() => {
                setSelectedModelForRetouches(null);
                fetchData();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelsManagement;
